import { useState, useCallback, useEffect } from 'react';
import { SCREENS } from '../constants';

// Navigation state storage key
const NAVIGATION_STATE_KEY = 'navigationState';

/**
 * Custom hook for managing navigation state
 * Provides navigation history, state persistence, and screen management
 */
export const useNavigation = (initialScreen = SCREENS.LOGIN) => {
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [navigationStack, setNavigationStack] = useState([]);
  const [previousScreen, setPreviousScreen] = useState(null);
  const [screenData, setScreenData] = useState({});

  // Load navigation state from localStorage on mount
  useEffect(() => {
    try {
      const savedNavState = localStorage.getItem(NAVIGATION_STATE_KEY);
      if (savedNavState) {
        const { screen, stack, previous, data } = JSON.parse(savedNavState);
        if (screen && screen !== SCREENS.LOGIN) {
          setCurrentScreen(screen);
          setNavigationStack(stack || []);
          setPreviousScreen(previous);
          setScreenData(data || {});
        }
      }
    } catch (error) {
      console.error('Error loading navigation state:', error);
    }
  }, []);

  // Save navigation state to localStorage
  const saveNavigationState = useCallback((screen, stack, previous, data) => {
    try {
      localStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify({
        screen,
        stack,
        previous,
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error saving navigation state:', error);
    }
  }, []);

  /**
   * Navigate to a screen
   * @param {string} screen - Target screen
   * @param {object} options - Navigation options
   */
  const navigateTo = useCallback((screen, options = {}) => {
    const { 
      replace = false, 
      data = null, 
      clearStack = false 
    } = options;

    console.log(`Navigating to ${screen}`, { replace, data, clearStack });

    let newStack;
    let newPrevious;

    if (clearStack) {
      newStack = [screen];
      newPrevious = null;
    } else if (replace) {
      // Replace current screen in stack
      newStack = [...navigationStack];
      if (newStack.length > 0) {
        newStack[newStack.length - 1] = screen;
      } else {
        newStack.push(screen);
      }
      newPrevious = previousScreen;
    } else {
      // Add to stack
      newPrevious = currentScreen;
      newStack = [...navigationStack, screen];
    }

    setNavigationStack(newStack);
    setPreviousScreen(newPrevious);
    setCurrentScreen(screen);

    // Store screen-specific data
    let newScreenData = { ...screenData };
    if (data) {
      newScreenData = {
        ...screenData,
        [screen]: data
      };
      setScreenData(newScreenData);
    }

    // Save state
    saveNavigationState(screen, newStack, newPrevious, newScreenData);
  }, [currentScreen, navigationStack, previousScreen, screenData, saveNavigationState]);

  /**
   * Go back to previous screen
   * @param {object} options - Back navigation options
   */
  const goBack = useCallback((options = {}) => {
    const { defaultScreen = SCREENS.CALENDAR } = options;

    console.log('Going back from', currentScreen, 'to', previousScreen);

    // Pop from stack
    const newStack = [...navigationStack];
    if (newStack.length > 0) {
      newStack.pop();
    }

    // Determine target screen
    let targetScreen = previousScreen;
    let newPrevious = null;

    if (!targetScreen && newStack.length > 0) {
      targetScreen = newStack[newStack.length - 1];
      newPrevious = newStack.length > 1 ? newStack[newStack.length - 2] : null;
    }
    
    if (!targetScreen) {
      targetScreen = defaultScreen;
      newStack.push(targetScreen);
    } else {
      // Set previous based on stack
      newPrevious = newStack.length > 1 ? newStack[newStack.length - 2] : null;
    }

    setNavigationStack(newStack);
    setCurrentScreen(targetScreen);
    setPreviousScreen(newPrevious);

    // Save state
    saveNavigationState(targetScreen, newStack, newPrevious, screenData);
  }, [currentScreen, previousScreen, navigationStack, screenData, saveNavigationState]);

  /**
   * Navigate to detail screen with source tracking
   * @param {object} data - Data to pass to detail screen
   * @param {string} sourceScreen - Source screen identifier
   */
  const navigateToDetail = useCallback((data, sourceScreen = null) => {
    const source = sourceScreen || currentScreen;
    console.log('Navigating to detail from', source);
    
    navigateTo(SCREENS.DETAIL, {
      data: { ...data, sourceScreen: source }
    });
  }, [currentScreen, navigateTo]);

  /**
   * Reset navigation to initial state
   */
  const resetNavigation = useCallback(() => {
    setCurrentScreen(SCREENS.LOGIN);
    setNavigationStack([]);
    setPreviousScreen(null);
    setScreenData({});
    
    // Clear saved state
    try {
      localStorage.removeItem(NAVIGATION_STATE_KEY);
    } catch (error) {
      console.error('Error clearing navigation state:', error);
    }
  }, []);

  /**
   * Get data for current screen
   * @returns {object} Screen data
   */
  const getCurrentScreenData = useCallback(() => {
    return screenData[currentScreen] || {};
  }, [screenData, currentScreen]);

  /**
   * Check if can go back
   * @returns {boolean} True if can go back
   */
  const canGoBack = useCallback(() => {
    return navigationStack.length > 1 || previousScreen !== null;
  }, [navigationStack, previousScreen]);

  /**
   * Get navigation breadcrumb
   * @returns {Array} Navigation breadcrumb
   */
  const getBreadcrumb = useCallback(() => {
    return navigationStack.map(screen => ({
      screen,
      title: getScreenTitle(screen)
    }));
  }, [navigationStack]);

  /**
   * Clear screen data for specific screen
   * @param {string} screen - Screen to clear data for
   */
  const clearScreenData = useCallback((screen) => {
    setScreenData(prev => {
      const newData = { ...prev };
      delete newData[screen];
      return newData;
    });
  }, []);

  return {
    // State
    currentScreen,
    previousScreen,
    navigationStack,
    screenData: getCurrentScreenData(),
    
    // Actions
    navigateTo,
    navigateToDetail,
    goBack,
    resetNavigation,
    clearScreenData,
    
    // Utilities
    canGoBack: canGoBack(),
    breadcrumb: getBreadcrumb(),
    
    // Debug info (remove in production)
    _debug: process.env.NODE_ENV === 'development' ? {
      stack: navigationStack,
      previous: previousScreen,
      allData: screenData,
      currentData: getCurrentScreenData()
    } : {}
  };
};

/**
 * Get screen title for display
 * @param {string} screen - Screen identifier
 * @returns {string} Screen title
 */
const getScreenTitle = (screen) => {
  const titles = {
    [SCREENS.LOGIN]: 'Đăng nhập',
    [SCREENS.CALENDAR]: 'Lịch',
    [SCREENS.HISTORY]: 'Lịch sử',
    [SCREENS.DETAIL]: 'Chi tiết',
    [SCREENS.CREATE_SHIPMENT]: 'Tạo chuyến'
  };
  return titles[screen] || screen;
};

export default useNavigation;